import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
  makeStyles,
  tokens,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Spinner,
  Field,
  Input,
  Textarea,
  RadioGroup,
  Radio,
  Switch,
  Badge,
  Text,
} from "@fluentui/react-components";
import {
  Add20Regular,
  Edit20Regular,
  Delete20Regular,
  Dismiss24Regular,
} from "@fluentui/react-icons";
import { SmsTemplate, PLACEHOLDERS, createEmptyTemplate } from "./types";
import { useTemplates } from "./useTemplates";
import { useSettings } from "../settings/useSettings";
import { computeSegments } from "../manual-sms/segments";
import { ScrollFade, useScrollFade } from "../../components/ScrollFade";
import { useTranslation } from "react-i18next";

interface TemplatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsert?: (text: string) => void;
  mailMode?: boolean;
}

const useStyles = makeStyles({
  surface: {
    maxWidth: "92vw",
    width: "460px",
    height: "min(85vh, 640px)",
    // Flex column so DialogBody stretches to the full height; capped so it
    // doesn't look oversized on tall panes.
    display: "flex",
    flexDirection: "column",
  },
  // Keep DialogBody's own grid (the title's action icon relies on it); just let
  // it grow to fill the surface so its 1fr content row can scroll internally.
  body: { flexGrow: 1, minHeight: 0 },
  content: { minHeight: 0, overflowY: "auto" },
  counter: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textAlign: "right",
    marginTop: "-4px",
  },
  list: { display: "flex", flexDirection: "column", gap: "8px" },
  item: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "8px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  itemHeader: { display: "flex", alignItems: "center", gap: "8px" },
  itemName: { fontWeight: tokens.fontWeightSemibold, flexGrow: 1 },
  preview: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  itemActions: { display: "flex", gap: "4px", justifyContent: "flex-end" },
  empty: { color: tokens.colorNeutralForeground3, textAlign: "center", padding: "16px" },
  syncWarning: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontSize: tokens.fontSizeBase200,
    padding: "6px 8px",
  },
  editor: { display: "flex", flexDirection: "column", gap: "12px" },
  chips: { display: "flex", flexWrap: "wrap", gap: "4px" },
});

const TemplatesDialog: React.FC<TemplatesDialogProps> = ({
  open,
  onOpenChange,
  onInsert,
  mailMode,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { settings } = useSettings();
  const { templates, upsert, remove, saving, syncError } = useTemplates();
  // Each surface shows only what it can actually use. Calendar automation
  // resolves meeting templates only, so listing mail snippets here invites a
  // user to reference one by name and silently get the default instead.
  const visibleTemplates = templates.filter((tpl) =>
    mailMode ? !tpl.isMeetingTemplate : tpl.isMeetingTemplate
  );
  const [draft, setDraft] = useState<SmsTemplate | null>(null); // non-null = editing
  const [nameError, setNameError] = useState<string>();
  const textRef = useRef<HTMLTextAreaElement>(null);
  const { scrollRef, showFade, updateFade, scrollDown } = useScrollFade();

  const encoding = settings?.encoding ?? "gsm7";

  useEffect(() => {
    if (!open) {
      setDraft(null);
      setNameError(undefined);
    }
  }, [open]);

  useEffect(updateFade, [open, draft, templates, updateFade]);

  const startNew = () => {
    setNameError(undefined);
    setDraft(createEmptyTemplate(!mailMode));
  };
  const startEdit = (tpl: SmsTemplate) => {
    setNameError(undefined);
    setDraft({ ...tpl });
  };

  const insertPlaceholder = (placeholder: string) => {
    if (!draft) return;
    const el = textRef.current;
    if (el) {
      const { selectionStart: s, selectionEnd: e } = el;
      const next = draft.text.slice(0, s) + placeholder + draft.text.slice(e);
      setDraft({ ...draft, text: next });
    } else {
      setDraft({ ...draft, text: draft.text + placeholder });
    }
  };

  const saveDraft = async () => {
    if (!draft) return;
    const name = draft.name.trim();
    if (!name) {
      setNameError(t("templates.editor.nameRequired"));
      return;
    }
    const duplicate = templates.some(
      (tpl) => tpl.id !== draft.id && tpl.name.trim().toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      setNameError(t("templates.editor.nameDuplicate"));
      return;
    }
    await upsert({ ...draft, name });
    setDraft(null);
  };

  const renderList = () => (
    <div className={styles.list}>
      {syncError && (
        <Text className={styles.syncWarning}>
          {t("templates.syncWarning", { reason: syncError })}
        </Text>
      )}
      {visibleTemplates.length === 0 ? (
        <Text className={styles.empty}>{t("templates.empty")}</Text>
      ) : (
        visibleTemplates.map((tpl) => (
          <div key={tpl.id} className={styles.item}>
            <div className={styles.itemHeader}>
              <Text className={styles.itemName}>{tpl.name}</Text>
              {!mailMode && tpl.isDefault && (
                <Badge appearance="tint" color="brand" size="small">
                  {t("templates.badge.default")}
                </Badge>
              )}
              {!mailMode && (
                <Badge appearance="outline" size="small">
                  {tpl.timing === "scheduled"
                    ? t("templates.badge.hoursBefore", { hours: tpl.hoursBefore })
                    : t("templates.badge.immediate")}
                </Badge>
              )}
            </div>
            <Text className={styles.preview}>{tpl.text || t("templates.emptyPreview")}</Text>
            <div className={styles.itemActions}>
              {onInsert && (
                <Button
                  size="small"
                  appearance="primary"
                  onClick={() => {
                    onInsert(tpl.text);
                    onOpenChange(false);
                  }}
                >
                  {t("templates.actions.use")}
                </Button>
              )}
              <Button
                size="small"
                appearance="subtle"
                icon={<Edit20Regular />}
                aria-label={t("templates.actions.edit")}
                onClick={() => startEdit(tpl)}
              />
              <Button
                size="small"
                appearance="subtle"
                icon={<Delete20Regular />}
                aria-label={t("templates.actions.delete")}
                disabled={saving}
                onClick={() => remove(tpl.id)}
              />
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderEditor = (tpl: SmsTemplate) => (
    <div className={styles.editor}>
      <Field
        label={t("templates.editor.name")}
        required
        validationState={nameError ? "error" : "none"}
        validationMessage={nameError}
      >
        <Input value={tpl.name} onChange={(_, d) => setDraft({ ...tpl, name: d.value })} />
      </Field>

      <Field
        label={t("templates.editor.message")}
        hint={mailMode ? t("templates.editor.hintMail") : t("templates.editor.hintMeeting")}
      >
        <Textarea
          ref={textRef}
          value={tpl.text}
          onChange={(_, d) => setDraft({ ...tpl, text: d.value })}
          rows={3}
          resize="vertical"
        />
      </Field>
      <Text className={styles.counter}>
        {(() => {
          const seg = computeSegments(tpl.text, encoding);
          // Placeholders resolve to real event data at send time, so the count
          // is only an estimate and "remaining" can't be known.
          const hasPlaceholder = PLACEHOLDERS.some((p) => tpl.text.includes(p));
          // The Message hint above already explains placeholder resolution, so
          // the estimate case only needs "≈" — keeps the counter on one line.
          return hasPlaceholder
            ? t("common.counter.estimate", { count: seg.length, segments: seg.segments })
            : t("common.counter.exact", {
                count: seg.length,
                segments: seg.segments,
                remaining: seg.remaining,
              });
        })()}
      </Text>
      {!mailMode && (
        <div className={styles.chips}>
          {PLACEHOLDERS.map((p) => (
            <Button key={p} size="small" appearance="outline" onClick={() => insertPlaceholder(p)}>
              {p}
            </Button>
          ))}
        </div>
      )}

      {!mailMode && (
        <>
          <Field label={t("templates.editor.timing")}>
            <RadioGroup
              value={tpl.timing}
              onChange={(_, d) => setDraft({ ...tpl, timing: d.value as SmsTemplate["timing"] })}
            >
              <Radio value="immediate" label={t("templates.editor.timingImmediate")} />
              <Radio value="scheduled" label={t("templates.editor.timingScheduled")} />
            </RadioGroup>
          </Field>
          {tpl.timing === "scheduled" && (
            <Field label={t("templates.editor.hoursBefore")}>
              <Input
                type="number"
                min={1}
                value={String(tpl.hoursBefore)}
                onChange={(_, d) =>
                  setDraft({ ...tpl, hoursBefore: Math.max(1, Number(d.value) || 1) })
                }
              />
            </Field>
          )}

          <Switch
            checked={tpl.isDefault}
            onChange={(_, d) => setDraft({ ...tpl, isDefault: d.checked })}
            label={t("templates.editor.isDefault")}
          />
        </>
      )}
    </div>
  );

  const editing = draft !== null;

  return (
    <Dialog open={open} onOpenChange={(_, d) => onOpenChange(d.open)}>
      <DialogSurface className={styles.surface}>
        <DialogBody className={styles.body}>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                size="small"
                icon={<Dismiss24Regular />}
                aria-label={editing ? t("templates.actions.back") : t("templates.actions.close")}
                onClick={() => (editing ? setDraft(null) : onOpenChange(false))}
              />
            }
          >
            {editing ? t("templates.titleOne") : t("templates.title")}
          </DialogTitle>
          <DialogContent className={styles.content} ref={scrollRef} onScroll={updateFade}>
            {editing && draft ? renderEditor(draft) : renderList()}
            <ScrollFade visible={showFade} onClick={scrollDown} />
          </DialogContent>
          <DialogActions>
            {editing ? (
              <Button
                appearance="primary"
                onClick={saveDraft}
                disabled={saving}
                icon={saving ? <Spinner size="tiny" /> : undefined}
              >
                {saving ? t("templates.actions.saving") : t("templates.actions.save")}
              </Button>
            ) : (
              <Button appearance="primary" icon={<Add20Regular />} onClick={startNew}>
                {t("templates.actions.new")}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default TemplatesDialog;
